/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Old Way vs FalcoCut comparison section for Face Swap
 */
import React from 'react';

import StartButton from './StartButton';

interface ComparisonItem {
  label: string;
  value: string;
}

const oldWayItems: ComparisonItem[] = [
  { label: 'Time Invested', value: '40\u201360 hours' },
  {
    label: 'Cost of Production',
    value: '$1,000+ for actors, studio, and editing',
  },
  {
    label: 'Team Required',
    value: 'Scriptwriter, videographer, editor, presenter',
  },
  { label: 'Quality', value: 'Inconsistent and amateur' },
  {
    label: 'Workflow',
    value:
      'Hiring actors, scheduling shoots, recording audio, editing, multiple reshoots',
  },
];

const newWayItems: ComparisonItem[] = [
  { label: 'Time Invested', value: '10 minutes' },
  {
    label: 'Cost of Production',
    value: 'From a few dollars per video \u2014 no actors or studio required',
  },
  { label: 'Team Required', value: 'Just you' },
  { label: 'Quality', value: 'Consistent, realistic, and professional' },
  {
    label: 'Workflow',
    value: 'Upload a video \u2192 select target face \u2192 generate instantly',
  },
];

const ComparisonSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Old Way vs. FalcoCut
        </h2>

        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Skip the long hours, high costs, and messy workflows of traditional video
          shoots. With FalcoCut, you can create professional, studio-quality avatar videos
          in just minutes&mdash;no actors, cameras, or editing teams required.
        </p>

        <div className="grid grid-cols-1 pad:grid-cols-2 gap-6 pad:gap-8 mb-10 pad:mb-14">
          {/* Old Way Card */}
          <div className="rounded-2xl border border-gray-200 p-6 pad:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">Old Way</h3>
            </div>

            <ul className="space-y-4 mb-6">
              {oldWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-gray-400 mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">
                    <span className="font-medium">{item.label}:</span> {item.value}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
              <span>{'\uD83D\uDE2B'} Complex, expensive, and time-consuming.</span>
            </div>
          </div>

          {/* New Way Card */}
          <div className="rounded-2xl border-2 border-[#7C3AED] bg-[#7C3AED]/5 p-6 pad:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">
                New Way with FalcoCut
              </h3>
            </div>

            <ul className="space-y-4 mb-6">
              {newWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-[#7C3AED] mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">
                    <span className="font-medium">{item.label}:</span> {item.value}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-base font-semibold text-[#7C3AED]">
              <span>{'\uD83D\uDE80'} Fast, affordable, and professional.</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <StartButton text="Swap Your Face" />
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
